import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, map, skipWhile } from 'rxjs';
import { selectUserPermissions } from '../state/core.selectors';

@Injectable({
    providedIn: 'root'
})
export class AuthGuardService {
    constructor(private readonly store: Store, private readonly router: Router) {}

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
        return this.store.select(selectUserPermissions).pipe(
            skipWhile(permissions => permissions == null),
            map(permissions => {
                return this.processPermissions(route, permissions);
            })
        );
    }

    canActivateChild(childRoute: ActivatedRouteSnapshot): Observable<boolean> {
        return this.store.select(selectUserPermissions).pipe(
            skipWhile(permissions => permissions == null),
            map(permissions => {
                return this.processPermissions(childRoute, permissions);
            })
        );
    }

    processPermissions(route: ActivatedRouteSnapshot, permissions?: string[]): boolean {
        let hasPermission = permissions && permissions?.length > 0 ? true : false;
        if (hasPermission && route.data) {
            const authGuardData = this.parseRouteData(route);
            hasPermission = this.hasRoutePermissions(authGuardData, permissions) && !this.hasRouteBlockPermissions(authGuardData, permissions);
            if (!hasPermission && authGuardData.redirectOnFail) {
                this.handleRedirectOnFail(authGuardData.redirectOnFail);
            }
        }
        return hasPermission;
    }

    handleRedirectOnFail(route: string) {
        this.router.navigateByUrl(route);
    }

    parseRouteData(route: ActivatedRouteSnapshot): AuthGuardData {
        return route.data as AuthGuardData;
    }

    hasRoutePermissions(authGuardData?: AuthGuardData, permissions?: string[]): boolean {
        if (this.isPermissionSecureRoute(authGuardData)) {
            if (permissions && permissions.length > 0) {
                return authGuardData!.expectedPermissions!.some((permission: string) => permissions.includes(permission));
            } else {
                return false;
            }
        }
        return true;
    }

    hasRouteBlockPermissions(authGuardData?: AuthGuardData, permissions?: string[]) {
        if (this.isPermissionBlockSecureRoute(authGuardData)) {
            if (permissions && permissions.length > 0) {
                return authGuardData!.blockPermissions!.some((permission: string) => permissions.includes(permission));
            }
        }
        return false;
    }

    isPermissionSecureRoute(authGuardData?: AuthGuardData): boolean {
        return authGuardData && Array.isArray(authGuardData.expectedPermissions) && authGuardData.expectedPermissions.length > 0 ? true : false;
    }

    isPermissionBlockSecureRoute(authGuardData?: AuthGuardData): boolean {
        return authGuardData && Array.isArray(authGuardData.blockPermissions) && authGuardData.blockPermissions.length > 0 ? true : false;
    }
}

export const AuthGuard: CanActivateFn = (next: ActivatedRouteSnapshot): Observable<boolean> => inject(AuthGuardService).canActivate(next);

export const AuthGuardChild: CanActivateChildFn = (next: ActivatedRouteSnapshot): Observable<boolean> => inject(AuthGuardService).canActivateChild(next);

export interface AuthGuardData {
    expectedPermissions?: string[];
    blockPermissions?: string[];
    redirectOnFail?: string;
}
